import {
  Outlet,
  useLocation,
  useOutletContext,
  Navigate,
  matchPath,
} from "react-router-dom";
import { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import api from "../../../services/api";
import UnauthorizedPage from "../../../components/unauthorizedPage";

export async function checkIsPermission(route) {
  try {
    const response = await api.post("/interno/checkPermissionUser", {
      route: route,
    });

    return response?.data;
  } catch (error) {
    return false;
  }
}

export default function ProtectedRoutePermission({
  permissionPath,
  children,
  setAdmPermissao = () => { },
}) {
  const context = useOutletContext();
  const location = useLocation();

  const [status, setStatus] = useState("loading");
  // Mudamos o nome para refletir que estamos guardando a "rota base" verificada
  const [verifiedRoutePath, setVerifiedRoutePath] = useState(null);

  // 1. Função para descobrir qual "família" de rota precisa ser validada
  const getPathToVerify = (path) => {
    let p = permissionPath || path;
    if (!p) return p;

    // Agrupamento para Perfil
    // 1. Verifica primeiro a rota mais profunda/específica

    if (p.includes("/interno/home/perfil/servicos")) {
      return "/interno/home/perfil/servicos";
    }
    if (p.includes("/interno/home/perfil/config")) {
      return "/interno/home/perfil/config";
    }
    if (p.includes("/interno/home/perfil/circuito")) {
      return "/interno/home/perfil/circuito";
    }
    if (p.includes("/interno/home/perfil/remoteAccess")) {
      return "/interno/home/perfil/remoteAccess";
    }
    if (p.includes("/interno/home/perfil/flow")) {
      return "/interno/home/perfil/flow";
    }

    // Se o caminho contém apenas '/perfil/' seguido de ID, limpa para o perfil geral
    if (matchPath("/interno/home/perfil/:id", p)) {
      return "/interno/home/perfil";
    }

    // 3. Verifica a rota raiz do perfil por último (o matchPath padrão exige que seja exato se não configurarmos wildcard)
    if (matchPath("/interno/home/perfil", p)) {
      return "/interno/home/perfil";
    }
    // Central de Alertas: painel e configuração compartilham a mesma permissão
    if (p.includes("/interno/centralDeAlertas")) {
      return "/interno/centralDeAlertas";
    }

    if (p.includes("/interno/cadastroHosts")) {
      return "/interno/cadastroHosts";
    }

    if (p.includes("/interno/relatorio-reincidencia")) {
      return "/interno/relatorio-reincidencia";
    }

    // Agrupamento para Administração Geral (Abas de Relatório, Suporte e Permissões)
    // Isso garante que mudar entre essas abas não altere o currentPathToVerify
    if (p.includes("/administracao/geral")) {
      return "/administracao/geral";
    }

    return p;
  };

  // Avaliamos a rota base FORA do useEffect
  const currentPathToVerify = getPathToVerify(location.pathname);

  useEffect(() => {
    let isMounted = true;

    const verifyAccess = async () => {
      // 1. Verifica se a navegação está acontecendo dentro do mesmo "módulo pai"
      // Pega os primeiros 4 segmentos da URL (ex: ["", "interno", "home", "perfil"])
      const basePathAnterior = verifiedRoutePath
        ?.split("/")
        ?.slice(0, 4)
        ?.join("/");
      const basePathAtual = currentPathToVerify
        ?.split("/")
        ?.slice(0, 4)
        ?.join("/");

      const isSameModuleNavigation =
        verifiedRoutePath && basePathAnterior === basePathAtual;

      // 2. Só exibe o loading "tela cheia" se for a primeira vez carregando
      // ou se estiver mudando para um módulo completamente diferente
      if (!isSameModuleNavigation) {
        setStatus("loading");
      }

      try {
        await api.get("/authenticationToken");

        const userPermission = await checkIsPermission(currentPathToVerify);
        let hasAccess = false;

        if (userPermission?.isAdmin) {
          setAdmPermissao(userPermission?.isAdmin);
          hasAccess = true;
        } else {
          hasAccess = !!userPermission?.routesPermited;
        }

        if (isMounted) {
          setStatus(hasAccess ? "authorized" : "unauthorized");
          setVerifiedRoutePath(currentPathToVerify); // Salva a rota base como verificada
        }
      } catch (error) {
        if (isMounted) {
          setStatus("unauthenticated");
          setVerifiedRoutePath(currentPathToVerify);
        }
      }
    };

    // Só executa a requisição se a rota base atual for diferente da última que verificamos
    if (verifiedRoutePath !== currentPathToVerify) {
      verifyAccess();
    }

    return () => {
      isMounted = false;
    };
  }, [currentPathToVerify, permissionPath]); // Remova verifiedRoutePath das dependências se estiver

  // 2. Redirecionar se não autenticado
  if (status === "unauthenticated") {
    localStorage.clear();
    return <Navigate to="/interno/login" replace />;
  }

  // 3. Mostrar página de acesso negado
  if (status === "unauthorized") {
    return <UnauthorizedPage />;
  }

  // 4. Se não estiver autorizado ainda, NÃO exibimos o conteúdo.
  // MAS nós mantemos ele renderizado (com display: none) para não perder o estado.
  const isAuthorized = status === "authorized";
  const isLoading = status === "loading";

  return (
    <>
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "70vh",
          }}
        >
          <CircularProgress size={100} thickness={2.5} />
        </Box>
      )}

      {/* Renderizamos o Outlet sempre, mas escondemos se não estiver autorizado.
          Isso evita que o componente seja destruído e perca dados/faça re-fetch */}
      <Box sx={{ display: isAuthorized ? "block" : "none" }}>
        {children ? children : <Outlet context={context} />}
      </Box>
    </>
  );
}
